import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";

import Landing from "@/pages/Landing";
import SignInPage from "@/pages/SignInPage";
import SignUpPage from "@/pages/SignUpPage";
import Onboarding from "@/pages/Onboarding";
import StudentLayout from "@/components/StudentLayout";
import HomePage from "@/pages/student/HomePage";
import MySubjects from "@/pages/MySubjects";
import AttemptsPage from "@/pages/student/AttemptsPage";
import PracticePage from "@/pages/student/PracticePage";
import ProgressPage from "@/pages/student/ProgressPage";
import InsightsPage from "@/pages/student/InsightsPage";
import AttemptResult from "@/pages/student/AttemptResult";
import ExamPlayer from "@/pages/student/ExamPlayer";
import SubscriptionPage from "@/pages/student/SubscriptionPage";
import AccountSettings from "@/pages/AccountSettings";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminSubjects from "@/pages/admin/AdminSubjects";
import AdminUsers from "@/pages/admin/AdminUsers";
import QuestionBank from "@/pages/admin/QuestionBank";
import Syllabus from "@/pages/admin/Syllabus";
import Blueprints from "@/pages/admin/Blueprints";
import AICentre from "@/pages/admin/AICentre";
import ExamDates from "@/pages/admin/ExamDates";
import { Payments, Integrations, AuditLogs, Health } from "@/pages/admin/AdminTools";

function RequireAuth({ children }) {
  return (
    <>
      <Show when="signed-in">{children}</Show>
      <Show when="signed-out">
        <Navigate to="/sign-in" replace />
      </Show>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkAuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />

            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <Onboarding />
                </RequireAuth>
              }
            />

            <Route
              path="/app"
              element={
                <RequireAuth>
                  <StudentLayout />
                </RequireAuth>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="my-subjects" element={<MySubjects />} />
              <Route path="my-exams" element={<AttemptsPage />} />
              <Route path="practice" element={<PracticePage key="practice" title="Practice" blurb="Scaled sessions derived from the official pattern of your subject. Honest labels — these are not official simulations." />} />
              <Route path="mock-tests" element={<PracticePage key="mock" modes={["full"]} title="Full-length mock tests" blurb="Exact official structure: section counts, attempt limits, marking and duration from the verified blueprint." />} />
              <Route path="results" element={<ProgressPage />} />
              <Route path="insights" element={<InsightsPage />} />
              <Route path="results/:id" element={<AttemptResult />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="account" element={<AccountSettings />} />
            </Route>

            <Route path="/exam/:id" element={<RequireAuth><ExamPlayer /></RequireAuth>} />

            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <AdminLayout />
                </RequireAuth>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="subjects" element={<AdminSubjects />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="syllabus" element={<Syllabus />} />
              <Route path="question-bank" element={<QuestionBank />} />
              <Route path="blueprints" element={<Blueprints />} />
              <Route path="ai-centre" element={<AICentre />} />
              <Route path="exam-dates" element={<ExamDates />} />
              <Route path="payments" element={<Payments />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="health" element={<Health />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ClerkAuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Re-export for Clerk's dedicated page components used elsewhere
export { SignIn, SignUp };
